const MIN_LENGTH = 8;

const COMMON_PASSWORDS = new Set([
  'password',
  'password1',
  'password123',
  'password1234',
  '12345678',
  '123456789',
  '1234567890',
  'qwerty123',
  'qwertyuiop',
  'letmein',
  'welcome1',
  'abcd1234',
  'abcdefgh',
  'admin123',
  'iloveyou',
  'sunshine',
  'princess',
  'football',
  'baseball',
  'trustno1',
  'dragon',
  'master',
  'monkey',
  'shadow',
  'michael',
  'login',
  'abc123',
  '111111111',
  '000000000',
  'passw0rd',
]);

export type PasswordCheck = {
  key: string;
  label: string;
  met: boolean;
};

export type PasswordStrength = 'none' | 'weak' | 'fair' | 'strong';

export function getPasswordChecks(
  password: string,
  username?: string,
  email?: string,
): PasswordCheck[] {
  const trimmed = password.trim();
  const lower = password.toLowerCase();

  return [
    {
      key: 'length',
      label: 'At least 8 characters',
      met: trimmed.length >= MIN_LENGTH,
    },
    {
      key: 'noLeadingTrailingSpace',
      label: 'No leading or trailing spaces',
      met: password.length === 0 || password === trimmed,
    },
    {
      key: 'notCommon',
      label: 'Not a commonly used password',
      met: password.length === 0 || !COMMON_PASSWORDS.has(lower),
    },
    {
      key: 'notPersonal',
      label: "Doesn't contain your username or email",
      met:
        password.length === 0 ||
        ((!username ||
          username.length < 3 ||
          !lower.includes(username.toLowerCase())) &&
          (!email || !lower.includes(email.split('@')[0].toLowerCase()))),
    },
  ];
}

export function isPasswordValid(
  password: string,
  username?: string,
  email?: string,
): boolean {
  if (password.length === 0) return false;
  return getPasswordChecks(password, username, email).every(c => c.met);
}

export function getPasswordStrength(password: string): PasswordStrength {
  if (password.length === 0) return 'none';
  if (password.length < MIN_LENGTH) return 'weak';

  let score = 0;
  if (password.length >= 12) score++;
  if (password.length >= 16) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^a-zA-Z0-9]/.test(password)) score++;

  if (score <= 1) return 'weak';
  if (score <= 3) return 'fair';
  return 'strong';
}

export const STRENGTH_CONFIG: Record<
  PasswordStrength,
  { label: string; color: string; barFraction: number }
> = {
  none: { label: '', color: '#e2e8f0', barFraction: 0 },
  weak: { label: 'Weak', color: '#ef4444', barFraction: 0.33 },
  fair: { label: 'Fair', color: '#f59e0b', barFraction: 0.66 },
  strong: { label: 'Strong', color: '#22c55e', barFraction: 1 },
};
